import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Campaign {
  id: string;
  platform: string;
  campaignName: string;
  adsetName: string;
  adName: string;
  budgetSpent: number;
  createdAt: string;
  // Estado de la campaña: 'active' | 'paused' | 'finished'.
  status: string;
  // Presupuesto tope / límite (solo control, NO suma a la inversión global).
  budgetLimit: number;
  // Costo total invertido declarado al pausar/finalizar (sí suma al global).
  declaredCost: number;
}

export const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  active: 'Activa',
  paused: 'Pausada',
  finished: 'Finalizada',
};

// Una campaña cerrada (pausada o finalizada) consolida su inversión declarada
// en los reportes globales.
export function isCampaignClosed(status: string): boolean {
  return status === 'paused' || status === 'finished';
}

export interface CampaignsResult {
  data: Campaign[];
}

export const PLATFORM_LABELS: Record<string, string> = {
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  tiktok: 'TikTok',
  other: 'Otro',
};

export async function fetchCampaigns(): Promise<CampaignsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('campaigns').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Campaign[] = ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        platform: r.platform,
        campaignName: r.campaign_name,
        adsetName: r.adset_name ?? '',
        adName: r.ad_name ?? '',
        budgetSpent: Number(r.budget_spent ?? 0),
        createdAt: r.created_at ?? '',
        status: r.status ?? 'active',
        budgetLimit: Number(r.budget_limit ?? 0),
        declaredCost: Number(r.declared_cost ?? 0),
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[campaigns] connection failed:', err);
    throw err;
  }
}

export interface CampaignInput {
  platform: string;
  campaignName: string;
  adsetName: string;
  adName: string;
  // Presupuesto tope / límite (solo control).
  budgetLimit: number;
}

export async function saveCampaign(
  input: CampaignInput,
): Promise<{ data: Campaign | null; error: string | null }> {
  if (!(await canWrite('campaigns'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    platform: input.platform,
    campaign_name: input.campaignName.trim(),
    adset_name: input.adsetName.trim() || null,
    ad_name: input.adName.trim() || null,
    budget_limit: Number(input.budgetLimit) || 0,
    status: 'active',
  };

  try {
    const { data, error } = await supabase.from('campaigns').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la campaña guardada.' };
    recordAudit({
      entityType: 'campaigns',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.campaignName,
    });
    return {
      data: {
        id: row.id,
        platform: row.platform,
        campaignName: row.campaign_name,
        adsetName: row.adset_name ?? '',
        adName: row.ad_name ?? '',
        budgetSpent: Number(row.budget_spent ?? 0),
        createdAt: row.created_at ?? '',
        status: row.status ?? 'active',
        budgetLimit: Number(row.budget_limit ?? 0),
        declaredCost: Number(row.declared_cost ?? 0),
      },
      error: null,
    };
  } catch (err) {
    console.warn('[campaigns] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la campaña.' };
  }
}

// Cambia el estado de una campaña. Al pausarla o finalizarla se registra el
// costo total invertido declarado, que es lo único que consolida en los
// reportes globales (el presupuesto tope nunca suma).
export async function updateCampaignStatus(
  id: string,
  status: 'active' | 'paused' | 'finished',
  declaredCost?: number,
): Promise<{ error: string | null }> {
  if (!(await canWrite('campaigns'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const patch: Record<string, any> = { status };
  if (isCampaignClosed(status) && typeof declaredCost === 'number') {
    patch.declared_cost = Math.max(0, declaredCost);
  }

  try {
    const { error } = await supabase.from('campaigns').update(patch).eq('id', id);
    if (error) return { error: error.message };
    recordAudit({
      entityType: 'campaigns',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'status',
      newValue: status,
    });
    return { error: null };
  } catch (err) {
    console.warn('[campaigns] status update failed:', err);
    return { error: 'Error de conexión al actualizar la campaña.' };
  }
}

// Inversión consolidada global: solo las campañas pausadas/finalizadas.
export function consolidatedMarketingInvestment(campaigns: Campaign[]): number {
  return campaigns
    .filter((c) => isCampaignClosed(c.status))
    .reduce((sum, c) => sum + (c.declaredCost || 0), 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Métricas operativas de la campaña (registro diario / periódico).
// Cada fila guarda la inversión publicitaria, las conversaciones iniciadas,
// las ventas/conversiones y la facturación del período. Los indicadores
// derivados (CPA, ROAS, % de cierre) se calculan al vuelo, no se almacenan.
// ─────────────────────────────────────────────────────────────────────────────
export interface CampaignMetric {
  id: string;
  campaignId: string;
  entryDate: string;
  investment: number;
  conversations: number;
  sales: number;
  revenue: number;
  notes: string;
}

export interface CampaignMetricInput {
  id?: string;
  campaignId: string;
  entryDate: string;
  investment: number;
  conversations: number;
  sales: number;
  revenue: number;
  notes: string;
}

// Indicadores derivados de un conjunto de métricas (una fila o un acumulado).
export interface CampaignKpis {
  cpa: number;
  roas: number;
  conversion: number;
}

export function computeCampaignKpis(input: {
  investment: number;
  conversations: number;
  sales: number;
  revenue: number;
}): CampaignKpis {
  const cpa = input.conversations > 0 ? input.investment / input.conversations : 0;
  const roas = input.investment > 0 ? input.revenue / input.investment : 0;
  const conversion = input.conversations > 0 ? (input.sales / input.conversations) * 100 : 0;
  return { cpa, roas, conversion };
}

// Suma un conjunto de métricas en un total acumulado con sus indicadores.
export function sumCampaignMetrics(metrics: CampaignMetric[]) {
  const totals = metrics.reduce(
    (acc, m) => ({
      investment: acc.investment + m.investment,
      conversations: acc.conversations + m.conversations,
      sales: acc.sales + m.sales,
      revenue: acc.revenue + m.revenue,
    }),
    { investment: 0, conversations: 0, sales: 0, revenue: 0 },
  );
  return { ...totals, ...computeCampaignKpis(totals) };
}

export async function fetchCampaignMetrics(): Promise<CampaignMetric[]> {
  if (!isSupabaseConfigured || !supabase) return [];

  try {
    const { data, error } = await supabase.from('campaign_metrics').select('*');
    if (error) throw new Error(error.message);

    return ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        campaignId: r.campaign_id,
        entryDate: r.entry_date ?? '',
        investment: Number(r.investment ?? 0),
        conversations: Number(r.conversations ?? 0),
        sales: Number(r.sales ?? 0),
        revenue: Number(r.revenue ?? 0),
        notes: r.notes ?? '',
      }))
      .sort((a, b) => (a.entryDate < b.entryDate ? 1 : -1));
  } catch (err) {
    console.error('[campaigns] metrics fetch failed:', err);
    throw err;
  }
}

export async function saveCampaignMetric(
  input: CampaignMetricInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('campaigns'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  if (!input.campaignId) return { error: 'Selecciona una campaña.' };

  const payload = {
    campaign_id: input.campaignId,
    entry_date: input.entryDate,
    investment: Number(input.investment) || 0,
    conversations: Math.max(0, Math.round(Number(input.conversations) || 0)),
    sales: Math.max(0, Math.round(Number(input.sales) || 0)),
    revenue: Number(input.revenue) || 0,
    notes: input.notes.trim() || null,
    updated_at: new Date().toISOString(),
  };

  try {
    const res = input.id
      ? await supabase.from('campaign_metrics').update(payload).eq('id', input.id)
      : await supabase.from('campaign_metrics').insert(payload);
    if (res.error) return { error: res.error.message };
    recordAudit({
      entityType: 'campaign_metrics',
      entityId: input.id ?? input.campaignId,
      action: input.id ? 'UPDATE' : 'CREATE',
      newValue: input.entryDate,
    });
    return { error: null };
  } catch (err) {
    console.warn('[campaigns] metric save failed:', err);
    return { error: 'Error de conexión al guardar los datos de campaña.' };
  }
}

export async function deleteCampaignMetric(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('campaigns'))) return { error: DELETE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { error } = await supabase.from('campaign_metrics').delete().eq('id', id);
    if (error) return { error: error.message };
    recordAudit({ entityType: 'campaign_metrics', entityId: id, action: 'DELETE' });
    return { error: null };
  } catch (err) {
    console.warn('[campaigns] metric delete failed:', err);
    return { error: 'Error de conexión al eliminar el registro.' };
  }
}